import React, { useState, useEffect } from 'react';
import { getProjects, getPackage, createPackage, updatePackage, BASE_URL } from '../api';
import { useTheme } from '../ThemeContext';
import { useToast } from '../components/Toast';

export default function PackageBuilder() {
  const { colors, common, mode } = useTheme();
  const toast = useToast();
  const pathParts = window.location.pathname.split('/');
  const pkgId = pathParts[2] !== 'new' ? pathParts[2] : null;

  const [allSims, setAllSims]         = useState([]);
  const [selected, setSelected]       = useState([]); // { id, title }
  const [title, setTitle]             = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving]           = useState(false);
  const [exporting, setExporting]     = useState(false);
  const [exportingScorm, setExportingScorm] = useState(false);
  const [savedId, setSavedId]         = useState(pkgId);
  const [error, setError]             = useState('');
  const [saved, setSaved]             = useState(false);

  useEffect(() => {
    fetchSims();
    if (pkgId) loadPackage(pkgId);
  }, []);

  const fetchSims = async () => {
    try {
      const { data } = await getProjects();
      setAllSims(data);
    } catch (err) { console.error('Could not load simulations'); }
  };

  const loadPackage = async (id) => {
    try {
      const { data } = await getPackage(id);
      setTitle(data.title);
      setDescription(data.description || '');
      setSelected(data.simulations.map(s => ({ id: s.id, title: s.title })));
    } catch (err) { console.error('Could not load package'); }
  };

  const addSim = (sim) => {
    if (selected.find(s => s.id === sim.id)) return;
    setSelected(prev => [...prev, { id: sim.id, title: sim.title }]);
  };

  const removeSim = (id) => {
    setSelected(prev => prev.filter(s => s.id !== id));
  };

  const moveUp = (index) => {
    if (index === 0) return;
    setSelected(prev => {
      const next = [...prev];
      [next[index - 1], next[index]] = [next[index], next[index - 1]];
      return next;
    });
  };

  const moveDown = (index) => {
    setSelected(prev => {
      if (index === prev.length - 1) return prev;
      const next = [...prev];
      [next[index], next[index + 1]] = [next[index + 1], next[index]];
      return next;
    });
  };

  // Returns the package ID (existing or newly created), or null on error
  const doSave = async () => {
    if (!title.trim()) { setError('Please enter a package title'); return null; }
    if (selected.length === 0) { setError('Please add at least one simulation'); return null; }
    setError('');
    setSaving(true);
    let id = savedId;
    try {
      const payload = { title: title.trim(), description: description.trim(), simulationIds: selected.map(s => s.id) };
      if (id) {
        await updatePackage(id, payload);
      } else {
        const { data } = await createPackage(payload);
        id = data.id;
        setSavedId(id);
        window.history.replaceState({}, '', `/packages/${id}`);
      }
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      setError('Could not save package. Please try again.');
      id = null;
    }
    setSaving(false);
    return id;
  };

  const handleSave = () => doSave();

  const handleExportHTML = async () => {
    const id = await doSave();
    if (!id) return;
    setExporting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BASE_URL}/api/packages/${id}/export`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Export failed');
      const blob = await response.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href = url; a.download = `${title || 'package'}_package.zip`; a.click();
      URL.revokeObjectURL(url);
    } catch (err) { toast('Export failed. Please try again.', 'error'); }
    setExporting(false);
  };

  const handleExportScorm = async () => {
    const id = await doSave();
    if (!id) return;
    setExportingScorm(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BASE_URL}/api/packages/${id}/export/scorm`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Export failed');
      const blob = await response.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href = url; a.download = `${title || 'package'}_package_scorm.zip`; a.click();
      URL.revokeObjectURL(url);
    } catch (err) { toast('SCORM export failed. Please try again.', 'error'); }
    setExportingScorm(false);
  };

  const availableSims = allSims.filter(s => !selected.find(sel => sel.id === s.id));

  const sectionHeading = {
    margin: '0 0 12px 0', color: colors.textPrimary, fontSize: 15, fontWeight: 700,
  };

  const simRowStyle = {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '10px 14px', borderRadius: 8, marginBottom: 6,
    border: `1px solid ${colors.borderColor}`, backgroundColor: colors.cardBg,
    gap: 8,
  };

  return (
    <div style={{ ...common.page, minHeight: 'unset', flex: 1 }}>
      <header style={common.header}>
        <div style={common.headerLeft}>
          <a href="/dashboard" aria-label="Back to dashboard" style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
            <img src={mode === 'dark' ? '/Logo-Dark.png' : '/Logo-Light.png'} alt="Software Simulation Builder logo" style={{ height: 32, width: 'auto' }} />
          </a>
          <a href="/dashboard" style={{ ...common.btn, ...common.btnGhost, textDecoration: 'none' }}>← Back</a>
          <h1 style={common.appTitle}>{pkgId ? 'Edit Package' : 'New Package'}</h1>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {saved && (
            <span style={{ color: '#22c55e', fontSize: 14, fontWeight: 600, alignSelf: 'center' }}>✓ Saved</span>
          )}
          <button style={{ ...common.btn, ...common.btnPrimary, opacity: saving ? 0.7 : 1 }}
            onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : savedId ? 'Save Changes' : 'Save Package'}
          </button>
          <button
            style={{ ...common.btn, ...common.btnPrimary, opacity: exporting ? 0.7 : 1 }}
            onClick={handleExportHTML} disabled={exporting || saving}>
            {exporting ? '⏳ Exporting...' : '⬇ Export HTML'}
          </button>
          <button
            style={{ ...common.btn, ...common.btnPrimary, opacity: exportingScorm ? 0.7 : 1 }}
            onClick={handleExportScorm} disabled={exportingScorm || saving}>
            {exportingScorm ? '⏳ Exporting...' : '⬇ Export SCORM'}
          </button>
        </div>
      </header>

      <main id="main-content" style={common.content}>
        {error && <p role="alert" style={{ ...common.errorText, marginBottom: 16 }}>{error}</p>}

        {/* Package details */}
        <div style={{ ...common.card, marginBottom: 32 }}>
          <div style={{ padding: 24 }}>
            <h2 style={{ ...common.sectionHeading, marginBottom: 16 }}>Package Details</h2>
            <label htmlFor="pkg-title" style={common.label}>Package Title</label>
            <input id="pkg-title" style={common.input} type="text"
              placeholder="e.g. Microsoft Office Training"
              value={title} onChange={e => setTitle(e.target.value)} />
            <label htmlFor="pkg-desc" style={common.label}>Description (optional)</label>
            <textarea id="pkg-desc" style={{ ...common.textarea, marginBottom: 0 }}
              placeholder="What does this package cover?"
              value={description} onChange={e => setDescription(e.target.value)} rows={2} />
          </div>
        </div>

        {/* Two-column builder */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>

          {/* Left: available simulations */}
          <div style={common.card}>
            <div style={{ padding: 24 }}>
              <h2 style={sectionHeading}>Available Simulations</h2>
              <p style={{ color: colors.textSecondary, fontSize: 13, marginBottom: 16 }}>
                Click a simulation to add it to the package
              </p>
              {availableSims.length === 0 ? (
                <p style={{ color: colors.textMuted, fontSize: 14 }}>
                  {allSims.length === 0 ? 'No simulations found. Create one from the dashboard first.' : 'All simulations have been added.'}
                </p>
              ) : (
                availableSims.map(sim => (
                  <div key={sim.id} style={simRowStyle}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ color: colors.textPrimary, fontSize: 14, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {sim.title}
                      </div>
                      {sim.description && (
                        <div style={{ color: colors.textMuted, fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {sim.description}
                        </div>
                      )}
                    </div>
                    <button style={{ ...common.btn, ...common.btnPrimary, padding: '6px 14px', flexShrink: 0 }}
                      onClick={() => addSim(sim)}>+ Add</button>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Right: package contents */}
          <div style={common.card}>
            <div style={{ padding: 24 }}>
              <h2 style={sectionHeading}>Package Contents</h2>
              <p style={{ color: colors.textSecondary, fontSize: 13, marginBottom: 16 }}>
                {selected.length === 0
                  ? 'No simulations added yet'
                  : `${selected.length} simulation${selected.length !== 1 ? 's' : ''} — learners will complete them in this order`}
              </p>
              {selected.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '32px 0', color: colors.textMuted, fontSize: 14 }}>
                  Add simulations from the left panel
                </div>
              ) : (
                selected.map((sim, index) => (
                  <div key={sim.id} style={simRowStyle}>
                    <span style={{
                      width: 28, height: 28, borderRadius: '50%', background: colors.indigo,
                      color: '#fff', fontSize: 13, fontWeight: 700,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    }}>{index + 1}</span>
                    <span style={{ flex: 1, color: colors.textPrimary, fontSize: 14, fontWeight: 600, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {sim.title}
                    </span>
                    <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                      <button style={{ ...common.btn, ...common.btnGhost, padding: '4px 8px', fontSize: 13 }}
                        onClick={() => moveUp(index)} disabled={index === 0} aria-label="Move up">↑</button>
                      <button style={{ ...common.btn, ...common.btnGhost, padding: '4px 8px', fontSize: 13 }}
                        onClick={() => moveDown(index)} disabled={index === selected.length - 1} aria-label="Move down">↓</button>
                      <button style={{ ...common.btn, ...common.btnDanger, padding: '4px 10px', fontSize: 13 }}
                        onClick={() => removeSim(sim.id)} aria-label={`Remove ${sim.title}`}>×</button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
